import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ENTRY_STATE_PREFIX } from '../lib/scrollRestore';
import { session } from '../lib/safeStorage';

/** A blocked storage, a missing key or a damaged value gives `initial`. */
const read = <T>(storageKey: string, initial: T): T => session.getJson<T>(storageKey, initial);

/**
 * A value that belongs to the history entry on screen (#595): for example the
 * "Show more" count of a long list. Back to the entry gives the value back,
 * so the scroll restore finds the same rows; a new visit of the page starts
 * from `initial`. The value lives in `sessionStorage` under the key of the
 * entry. A blocked storage keeps the value for this page view only.
 *
 * The page can stay mounted while the entry changes: Back from `/athletes`
 * to `/athletes?gender=male`, or a filter that replaces the entry. The hook
 * then gives the value of the new entry, in a layout effect: the scroll
 * restore of Back looks at the page in the next frame. A new document in the
 * tab forgets the values of the key `default` (lib/scrollRestore.ts).
 */
export function useEntryState<T>(name: string, initial: T): [T, (value: T) => void] {
  const { key } = useLocation();
  const storageKey = `${ENTRY_STATE_PREFIX}${key}:${name}`;
  const [value, setValue] = useState<T>(() => read(storageKey, initial));
  const entry = useRef(storageKey);
  const initialRef = useRef(initial);
  useLayoutEffect(() => {
    if (entry.current === storageKey) return;
    entry.current = storageKey;
    setValue(read(storageKey, initialRef.current));
  }, [storageKey]);
  const set = useCallback((next: T) => {
    setValue(next);
    session.setJson(storageKey, next); // With the storage off, the value lasts for this page view only.
  }, [storageKey]);
  return [value, set];
}
